import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Quote } from "lucide-react";
import { Reveal, MaskLines, Eyebrow, EASE } from "@/components/Motion";
import { Loading, useApi } from "@/components/Common";
import { Lightbox } from "@/components/Lightbox";
import { Photo } from "@/components/Photo";
import { ageFrom, media } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const Glance = ({ p }) => (
  <div className="card-gold p-6 md:p-7" data-testid="at-a-glance">
    <p className="font-display text-2xl text-[#C9A24B]">{p.known_as} at a Glance</p>
    <dl className="mt-5 space-y-3 text-sm">
      {(p.glance || []).map(([k, v]) => (
        <div key={k} className="grid grid-cols-[120px_1fr] gap-3 border-b border-[#C9A24B]/15 pb-2">
          <dt className="text-[#E4C87F]">{k}</dt><dd className="text-[#F6EFE0]/90" data-testid={k === "Age" ? "glance-age" : undefined}>{v === "__AGE__" ? ageFrom(p.birth_date) : v || "Coming soon"}</dd>
        </div>
      ))}
    </dl>
    {(p.education || []).length > 0 && <><p className="mt-6 label">Education</p>
    <ul className="space-y-2 text-sm">{p.education.map(([y, t]) => <li key={y + t}><span className="font-mono-x text-[#C9A24B] mr-2">{y}</span>{t}</li>)}</ul></>}
  </div>
);

const Monogram = ({ letter, testid }) => (
  <div className="w-full h-full grid place-items-center bg-[radial-gradient(ellipse_at_center,#163B29,#0B1D14_75%)]" data-testid={testid}>
    <div className="grid place-items-center w-32 h-32 rounded-full border border-[#C9A24B]/60 shadow-[0_0_0_10px_rgba(201,162,75,.06)]">
      <span className="font-display text-6xl text-[#C9A24B]">{letter}</span>
    </div>
  </div>
);

const useNoIndex = (on) => {
  useEffect(() => {
    if (!on) return undefined;
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { m.remove(); };
  }, [on]);
};

export default function Personal() {
  const { slug } = useParams();
  const { session } = useSite() || {};
  const { data: p, loading, error } = useApi(`/personal/${slug}`, [slug, session?.active]);
  const [i, setI] = useState(null);
  useNoIndex(!!p?.noindex);
  if (error === 403 || error === 404) return <Navigate to="/gate" replace />;
  if (loading || !p) return <div className="pt-32"><Loading label="Opening by invitation" /></div>;
  const gallery = p.gallery_meta || [];
  return (
    <div data-testid="personal-page">
      <header className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(201,162,75,.18),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid gap-12 lg:grid-cols-[1.3fr_1fr] items-end">
          <div>
            <motion.p className="font-accent italic text-xl text-[#E4C87F]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} data-testid="personal-welcome">Welcome, you're here by invitation.</motion.p>
            <MaskLines lines={p.name.split(" ").length > 2 ? [p.name.split(" ").slice(0, 2).join(" "), p.name.split(" ").slice(2).join(" ")] : [p.name]} className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight" delay={0.3} testid="personal-name" />
            <Reveal delay={0.6}><p className="mt-6 max-w-xl text-lg text-[#F6EFE0]/85">{p.subtitle}</p></Reveal>
            {p.privacy_note && <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#C9A24B]/40 px-4 py-1.5 text-sm text-[#E4C87F]" data-testid="personal-privacy-note"><Lock size={14} /> {p.privacy_note}</p>}
          </div>
          <motion.div initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, ease: EASE }} className="photo-frame aspect-[4/5] max-w-sm w-full lg:justify-self-end group" data-testid="personal-portrait">
            {p.portrait_url ? <img src={media(p.portrait_url)} alt={`Portrait of ${p.known_as}`} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-[filter] duration-[1600ms]" /> : <Monogram letter={(p.known_as || p.name || "?")[0]} testid="personal-monogram" />}
          </motion.div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 pb-28 grid gap-12 lg:grid-cols-[200px_1fr_340px]">
        <nav className="hidden lg:block sticky top-28 self-start" data-testid="chapter-menu">
          <p className="label">Chapters</p>
          <ul className="mt-3 space-y-2 text-sm">{p.chapters.map((c) => <li key={c.key}><a href={`#${c.key}`} className="sweep text-[#F6EFE0]/80 hover:text-[#F6EFE0]" onClick={(e) => { e.preventDefault(); const el = document.getElementById(c.key); window.__lenis ? window.__lenis.scrollTo(el, { offset: -90 }) : el?.scrollIntoView({ behavior: "smooth" }); }} data-testid={`chapter-link-${c.key}`}>{c.title}</a></li>)}</ul>
        </nav>
        <article className="min-w-0">
          <Reveal><p className="font-accent text-2xl leading-relaxed text-[#F6EFE0]/95 first-letter:font-display first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:text-[#C9A24B]">{p.intro || "Coming soon"}</p></Reveal>
          {p.chapters.map((c, k) => (
            <section key={c.key} id={c.key} className="mt-20 scroll-mt-28" data-testid={`chapter-${c.key}`}>
              <Reveal><Eyebrow>Chapter {String(k + 1).padStart(2, "0")}</Eyebrow><h2 className="mt-3 font-display text-3xl md:text-4xl">{c.title}</h2></Reveal>
              {c.key !== "pictures" && gallery[k % Math.max(1, gallery.length)] && k % 3 === 0 && (
                <Reveal className="mt-8"><Photo src={gallery[k % gallery.length].url} era="sepia" caption={gallery[k % gallery.length].story || undefined} onClick={() => setI(k % gallery.length)} /></Reveal>
              )}
              {c.key === "pictures" ? (
                gallery.length ? (
                  <div className="mt-8 grid gap-4 grid-cols-1 sm:grid-cols-2" data-testid="personal-pictures">{gallery.map((g, n) => <Reveal key={g.id + n} delay={(n % 2) * 0.06}><Photo src={g.url} era={g.era} onClick={() => setI(n)} testid={`personal-picture-${n}`} /></Reveal>)}</div>
                ) : (
                  <div className="mt-8" data-testid="personal-pictures-empty"><div className="photo-frame aspect-[4/3] max-w-md"><Monogram letter={(p.known_as || p.name || "?")[0]} /></div><p className="mt-6 text-base md:text-lg text-[#F6EFE0]/85">Coming soon</p></div>
                )
              ) : (c.paragraphs?.length ? c.paragraphs : ["Coming soon"]).map((para, j) => (
                <Reveal key={j} delay={0.05}><p className="mt-6 text-base md:text-lg leading-relaxed text-[#F6EFE0]/85">{para}</p></Reveal>
              ))}
              {c.quote && (
                <Reveal className="my-12"><blockquote className="relative border-l-2 border-[#C9A24B] pl-8 py-4" data-testid="pull-quote"><Quote className="absolute -left-4 -top-3 text-[#C9A24B] bg-[#0B1D14]" /><p className="font-display italic text-5xl md:text-6xl text-[#E4C87F]">"{c.quote}"</p><p className="mt-3 text-sm text-[#F6EFE0]/75">Afrikaans for "I'm tired."</p></blockquote></Reveal>
              )}
            </section>
          ))}
          {(p.milestones || []).length > 0 && <section className="mt-24" data-testid="milestones">
            <Reveal><Eyebrow>Milestones</Eyebrow><h2 className="mt-3 font-display text-3xl md:text-4xl">A life in moments</h2></Reveal>
            <ol className="mt-10 relative border-l border-[#C9A24B]/40 ml-2">
              {(p.milestones || []).map(([y, t], k) => (
                <motion.li key={k} className="ml-8 mb-8 relative" initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ delay: k * 0.05, duration: 0.7 }}>
                  <span className="absolute -left-[38px] top-1.5 w-3 h-3 rounded-full bg-[#C9A24B] shadow-[0_0_0_5px_rgba(201,162,75,.15)]" />
                  <p className="font-display text-xl text-[#C9A24B]">{y}</p><p className="text-[#F6EFE0]/85">{t}</p>
                </motion.li>
              ))}
            </ol>
          </section>}
          {!p.chapters.some((c) => c.key === "pictures") && <section className="mt-20" data-testid="personal-gallery">
            <Reveal><Eyebrow>Gallery · from the family album</Eyebrow></Reveal>
            <div className="mt-8 grid gap-4 grid-cols-1 sm:grid-cols-2">
              {gallery.map((g, k) => <Reveal key={g.id + k} delay={(k % 2) * 0.06}><Photo src={g.url} era={g.era} onClick={() => setI(k)} testid={`personal-gallery-${k}`} /></Reveal>)}
            </div>
          </section>}
          <div className="lg:hidden mt-16"><Glance p={p} /></div>
        </article>
        <aside className="hidden lg:block sticky top-28 self-start"><Glance p={p} /></aside>
      </div>
      <Lightbox items={gallery} index={i} onClose={() => setI(null)} onIndex={setI} />
    </div>
  );
}
